import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ContentSection, NewsletterBand, PageHero } from "../components/Sections";
import { PageSeo } from "../components/PageContent";
import { contactPage } from "../content/pagesContent";
import { sharedContent } from "../content/siteContent";
import { contactFailureMessage, contactRequestPayload } from "../lib/contactResponse";

const initialStatus = { state: "idle", message: "" };
const localTurnstileSiteKey = "1x00000000000000000000BB";

async function getTurnstileSiteKey() {
  if (["localhost", "127.0.0.1"].includes(window.location.hostname)) return localTurnstileSiteKey;
  const response = await fetch("/api/contact/config");
  if (!response.ok) throw new Error("CAPTCHA configuration unavailable");
  const { turnstileSiteKey } = await response.json();
  if (!turnstileSiteKey) throw new Error("CAPTCHA site key unavailable");
  return turnstileSiteKey;
}

export default function ContactPage() {
  const [status, setStatus] = useState(initialStatus);
  const [captchaToken, setCaptchaToken] = useState("");
  const startedAt = useRef(Date.now());
  const captcha = useRef(null);

  async function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus({ state: "sending", message: "Sending your message..." });
    const payload = contactRequestPayload(new FormData(form), captchaToken);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await contactFailureMessage(response));
      }
      form.reset();
      captcha.current?.reset();
      setCaptchaToken("");
      startedAt.current = Date.now();
      setStatus({ state: "success", message: contactPage.success });
    } catch (error) {
      captcha.current?.reset();
      setCaptchaToken("");
      setStatus({ state: "error", message: error instanceof Error ? error.message : contactPage.failure });
    }
  }

  return (
    <>
      <PageSeo page={contactPage} />
      <PageHero content={contactPage.hero} />
      <ContentSection heading={contactPage.introduction.heading}>
        <div className="long-copy">{contactPage.introduction.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
      </ContentSection>
      <ContentSection className="contact-section" tone="ink">
        <span className="section-anchor" id="contact-form" aria-hidden="true" />
        <div className="contact-heading">
          <p className="eyebrow">{contactPage.formEyebrow}</p>
          <h2>Send A Message</h2>
          <p>{contactPage.formIntro}</p>
        </div>
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <FormField label="First Name" name="firstName" required autoComplete="given-name" />
            <FormField label="Last Name" name="lastName" required autoComplete="family-name" />
            <FormField label="Business Email" name="email" type="email" required autoComplete="email" />
            <FormField label="Company" name="company" required autoComplete="organization" />
            <FormField label="Job Title" name="jobTitle" autoComplete="organization-title" />
            <FormField label="Location / City" name="location" autoComplete="address-level2" />
            <FormField label="Phone" name="phone" type="tel" autoComplete="tel" />
            <label className="field">
              <span>I am interested in <span aria-hidden="true">*</span></span>
              <select name="interest" required defaultValue="">
                <option value="" disabled>Select one</option>
                {contactPage.interestOptions.map((option) => <option key={option}>{option}</option>)}
              </select>
            </label>
            <label className="field field-full">
              <span>{contactPage.contextLabel} <span aria-hidden="true">*</span></span>
              <textarea name="context" required maxLength="2000" rows="8" />
              <small>Maximum 2,000 characters. Do not include confidential or regulated information.</small>
            </label>
            <label className="field field-full">
              <span>Who owns the business result or investment decision?</span>
              <textarea name="owner" maxLength="300" rows="3" />
              <small>Maximum 300 characters.</small>
            </label>
            <label className="field field-full">
              <span>Is there a decision date or operational deadline?</span>
              <textarea name="deadline" maxLength="300" rows="3" />
              <small>Maximum 300 characters.</small>
            </label>
            <label className="honeypot" aria-hidden="true">
              <span>Website</span>
              <input name="website" type="text" tabIndex="-1" autoComplete="off" />
            </label>
            <input name="startedAt" type="hidden" value={startedAt.current} readOnly />
            <label className="consent field-full">
              <input name="consent" type="checkbox" value="yes" required />
              <span>{contactPage.consent} <Link to="/privacy-policy/">Read the Privacy Policy</Link>.</span>
            </label>
            <TurnstileCaptcha ref={captcha} onToken={setCaptchaToken} />
          </div>
          <button className="button button-primary submit-button" type="submit" disabled={status.state === "sending" || !captchaToken}>
            {status.state === "sending" ? "Sending..." : "Send Message"}
          </button>
          {status.message && <p className={`form-status is-${status.state}`} role="alert">{status.message}</p>}
        </form>
      </ContentSection>
      <ContentSection heading="Frequently Asked Questions">
        <div className="faq-list">
          {contactPage.faqs.map((faq) => (
            <details key={faq.question}>
              <summary>{faq.question}</summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </ContentSection>
      <NewsletterBand content={sharedContent.newsletter} />
    </>
  );
}

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector('script[data-levelup360-turnstile="true"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.levelup360Turnstile = "true";
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.append(script);
  });
}

const TurnstileCaptcha = forwardRef(function TurnstileCaptcha({ onToken }, ref) {
  const container = useRef(null);
  const widgetId = useRef(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    async function initialise() {
      try {
        const turnstileSiteKey = await getTurnstileSiteKey();
        await loadTurnstileScript();
        if (!active || !container.current) return;
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: turnstileSiteKey,
          callback: (token) => { onToken(token); setState("ready"); },
          "expired-callback": () => { onToken(""); setState("expired"); },
          "error-callback": () => { onToken(""); setState("error"); },
        });
        setState("ready");
      } catch {
        if (active) setState("error");
      }
    }
    initialise();
    return () => {
      active = false;
      if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
    };
  }, [onToken]);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
    },
  }), []);

  return (
    <div className="captcha-field field-full">
      <div ref={container} />
      <p className={`captcha-status is-${state}`} role="status">
        {state === "loading" && "Loading security check..."}
        {state === "expired" && "The security check expired. Please complete it again."}
        {state === "error" && "The security check is unavailable. Please refresh the page or try again later."}
      </p>
    </div>
  );
});

function FormField({ label, name, type = "text", required = false, autoComplete }) {
  return (
    <label className="field">
      <span>{label}{required && <> <span aria-hidden="true">*</span></>}</span>
      <input name={name} type={type} required={required} autoComplete={autoComplete} />
    </label>
  );
}
